import { Box } from '@mui/material';
import React, { useState } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PrimaryButton } from 'src/components/shared/Button/Button';
import { Divider } from 'src/components/shared/Divider/Divider';
import { Icon } from 'src/components/shared/Icon/Icon';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { RootState, useReduxDispatch } from 'src/store';
import { alertError, alertSuccess } from 'src/store/alerts/alerts.actions';
import { postRequest } from 'src/store/api-handler';
import {
  importActions,
  parseEurofinsAnalyzes
} from 'src/store/imports/imports.actions';
import { selectImportProducts } from 'src/store/imports/imports.selectors';
import styled from 'styled-components';
import { EurofinsList } from './EurofinsList';

export const EurofinsPage = () => {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const importProducts$ = useSelector((state: RootState) =>
    selectImportProducts(state.imports)
  );

  const [fileNames, setFileNames] = useState([]);
  const onFileDropped = (files: any) => {
    setFileNames(files.map((file: any) => file.name));
    dispatch(parseEurofinsAnalyzes(files));
  };

  const onImport = async () => {
    const { ok, error } = await postRequest(
      'imports/eurofins',
      importProducts$
    );
    if (ok) dispatch(alertSuccess(t('import.success')));
    else dispatch(alertError(error?.errors?.detail ?? t('import.failed')));
    dispatch(importActions.setImportProducts(null));
  };

  return (
    <React.Fragment>
      <Header1>
        {t('import.singular')} {t('import.eurofins')}
      </Header1>
      <Divider />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Dropzone
            onDrop={files => {
              onFileDropped(files);
            }}
            maxFiles={1}
          >
            {({ getRootProps, getInputProps }) => (
              <FileDropContainer {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <Box display="flex" alignItems="center" justifyContent="center">
                  <Icon type="la-cloud-upload-alt" className="mr-4" />
                  {t('file.dragdrop')}
                </Box>
              </FileDropContainer>
            )}
          </Dropzone>
          <div>
            <strong>{t('file.singular')}:</strong>
            <ul>
              {fileNames.map(fileName => (
                <li key={fileName}>{fileName}</li>
              ))}
            </ul>
          </div>
        </Box>
        <PrimaryButton disabled={importProducts$ == null} onClick={onImport}>
          {t('import.verb')}
        </PrimaryButton>
      </Box>
      <Divider />
      <EurofinsList importProducts={importProducts$} />
    </React.Fragment>
  );
};

const FileDropContainer = styled.div`
  padding: 1.5rem;
  text-align: center;
  border: 1px dashed var(--primary-color);
  margin-bottom: 1rem;
  cursor: pointer;
`;
